/**
 * Notification Component
 * Reusable notification system for displaying toast-style messages
 */

import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
    id: number;
    type: NotificationType;
    message: string;
}

interface NotificationContextValue {
    showNotification: (type: NotificationType, message: string, duration?: number) => void;
    dismissNotification: (id: number) => void;
    notifications: Notification[];
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
};

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const notificationIdRef = useRef(0);

    const showNotification = useCallback((type: NotificationType, message: string, duration = 5000) => {
        const id = ++notificationIdRef.current;
        setNotifications(prev => [...prev, { id, type, message }]);

        // Auto-dismiss after duration
        if (duration > 0) {
            setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== id));
            }, duration);
        }
    }, []);

    const dismissNotification = useCallback((id: number) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    }, []);

    return (
        <NotificationContext.Provider value={{ showNotification, dismissNotification, notifications }}>
            {children}
        </NotificationContext.Provider>
    );
};

interface NotificationContainerProps {
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
    position = 'top-right',
    top = 60,
    right = 16,
    bottom,
    left,
}) => {
    const { notifications, dismissNotification } = useNotification();

    if (notifications.length === 0) {
        return null;
    }

    const getPositionStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            position: 'fixed',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            maxWidth: '400px',
        };

        switch (position) {
            case 'top-right':
                return { ...base, top, right };
            case 'top-left':
                return { ...base, top, left };
            case 'bottom-right':
                return { ...base, bottom, right };
            case 'bottom-left':
                return { ...base, bottom, left };
            default:
                return { ...base, top, right };
        }
    };

    const getNotificationStyles = (type: NotificationType): React.CSSProperties => {
        const typeStyles = {
            error: {
                backgroundColor: '#fef2f2',
                borderColor: '#ef4444',
                color: '#991b1b',
            },
            success: {
                backgroundColor: '#f0fdf4',
                borderColor: '#22c55e',
                color: '#166534',
            },
            warning: {
                backgroundColor: '#fffbeb',
                borderColor: '#f59e0b',
                color: '#92400e',
            },
            info: {
                backgroundColor: '#eff6ff',
                borderColor: '#3b82f6',
                color: '#1e40af',
            },
        };

        return {
            padding: '12px 16px',
            borderRadius: '8px',
            borderLeft: `4px solid ${typeStyles[type].borderColor}`,
            ...typeStyles[type],
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            fontSize: '14px',
            lineHeight: '1.5',
        };
    };

    return (
        <div className="notification-container" style={getPositionStyles()}>
            {notifications.map((notification) => (
                <div
                    key={notification.id}
                    className={`notification notification-${notification.type}`}
                    style={getNotificationStyles(notification.type)}
                >
                    <span style={{ flex: 1 }}>{notification.message}</span>
                    <button
                        onClick={() => dismissNotification(notification.id)}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '18px',
                            lineHeight: '1',
                            opacity: 0.6,
                            color: 'inherit',
                        }}
                        title="Dismiss"
                        aria-label="Dismiss notification"
                    >
                        ×
                    </button>
                </div>
            ))}
        </div>
    );
};
